import { Attachment, CloseOutlined, DriveFolderUpload } from "@mui/icons-material";
import { Box, Button, Tooltip, Typography } from "@mui/material";
import theme from "lib/theme";
import React, { useState } from "react";
import { useDropzone, Accept } from "react-dropzone";

interface Document {
  name: string;
  url?: string;
  onDelete: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

interface FileDropSectionProps {
  icon?: React.ReactNode;
  accept: Accept;
  acceptHint?: React.ReactNode;
  onUpload?: (files: File[]) => void;
  documents?: Document[];
  loading?: boolean;
}

function FileDropSection({
  icon = <DriveFolderUpload color="primary" sx={{ fontSize: "32px" }} />,
  accept,
  acceptHint,
  onUpload,
  documents,
  loading,
}: FileDropSectionProps) {
  const { getRootProps, getInputProps, open, isFocused, isDragAccept, isDragReject } = useDropzone({
    noClick: true,
    noKeyboard: true,
    accept,
    onDrop: async (files) => {
      onUpload?.(files);
    },
  });

  return (
    <div>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {documents && (
          <Box>
            <Typography fontSize={"12px"} fontWeight={800}>
              Attached Documents
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                textOverflow: "ellipsis",
              }}
            >
              {documents.map((document: Document) => {
                return (
                  <Tooltip
                    key={document.url}
                    title={
                      <React.Fragment>
                        <img src={document.url} alt={document.name} loading="lazy" width={280} />
                      </React.Fragment>
                    }
                  >
                    <Box
                      key={document.url}
                      component="a"
                      href={document.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        border: `1px solid ${theme.palette.primary.main}`,
                        borderRadius: "4px",
                        p: 1,
                        color: theme.palette.primary.main,
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        fontFamily: "Catamaran",
                        textDecoration: "none",
                        position: "relative",
                      }}
                    >
                      <Attachment />
                      {document.name}
                      <Box
                        component="button"
                        type="button"
                        sx={{
                          height: "24px",
                          width: "24px",
                          background: theme.palette.primary.main,
                          color: "white",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border: 0,
                          borderRadius: "100%",
                          position: "absolute",
                          top: "-4px",
                          right: "-8px",
                          cursor: "pointer",
                        }}
                        onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                          e.preventDefault();
                          document.onDelete(e);
                        }}
                      >
                        <CloseOutlined />
                      </Box>
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          </Box>
        )}

        <Box>
          {acceptHint && (
            <Typography fontSize={"12px"}>
              <strong>Accepted formats:</strong> {acceptHint}
            </Typography>
          )}

          <Box
            {...getRootProps({
              sx: [
                {
                  height: "190px",
                  border: `1px dashed ${theme.palette.divider}`,
                  borderRadius: 1,
                  display: "grid",
                  placeItems: "center",
                },
                isFocused && { borderColor: theme.palette.primary.main },
                isDragAccept && { borderColor: theme.palette.success.main },
                isDragReject && { borderColor: theme.palette.error.main },
              ],
            })}
          >
            <Box sx={{ textAlign: "center" }}>
              <input {...getInputProps()} />

              {icon}

              <Typography color="text.secondary" fontWeight={"600"}>
                Drag and drop <br />
                or
              </Typography>
              <Button type="button" sx={{ mt: 0.5 }} variant="outlined" color="primary" onClick={open}>
                BROWSE FILES
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </div>
  );
}

export default FileDropSection;
